import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import Select from "react-select";
import { HiPencilSquare } from "react-icons/hi2";
import api from "../config/api";
import EditModal from "../components/EditModal";
import {
  useSupervisorFilters,
  type SupervisorFilters,
} from "../hooks/useSupervisorFilters";
import { toast } from "react-toastify";
import { syncSummary } from "../utils/syncSummary";
import { syncEntity } from "../utils/syncEntity";
import { errorMessage } from "../utils/errors";
import "./Supervisors.css";
import "../styles/buttons.css";
import { EMPTY_VALUE } from "../utils/display";
import { darkSelectStyles } from "../utils/selectStyles";
import { formatLanguage } from "../utils/language";
import {
  WITHOUT_ENTERPRISE,
  appendEnterpriseFilter,
  enterpriseFilterLabel,
  enterpriseFilterOptions,
} from "../utils/enterpriseFilter";
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from "../utils/pagination";

interface Supervisor {
  id: number;
  aia_supervisor_id: string;
  first_name: string;
  last_name: string;
  email: string;
  enterpriseId: number;
  enterprise?: {
    nom: string;
    country: string;
    language: string;
  };
}

/** The filters the API applies; the sort is not among them. */
type SupervisorQuery = Pick<
  SupervisorFilters,
  "search" | "enterprise" | "country" | "language"
>;

const Supervisors: React.FC = () => {
  const [supervisors, setSupervisors] = useState<Supervisor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedSupervisor, setSelectedSupervisor] =
    useState<Supervisor | null>(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // `?withoutEnterprise=true` opens the page on the supervisors belonging to no
  // enterprise — how the dashboard's Superviseurs card links to them. Read once, as the
  // initial filter state, so the filter can then be changed without the URL pulling it
  // back.
  const [searchParams] = useSearchParams();
  const initialFilters = useMemo<Partial<SupervisorFilters> | undefined>(
    () =>
      searchParams.get("withoutEnterprise") === "true"
        ? { enterprise: WITHOUT_ENTERPRISE }
        : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Use supervisor filters hook
  const {
    filters,
    filteredSupervisors,
    updateFilter,
    resetFilters,
    hasActiveFilters,
  } = useSupervisorFilters(supervisors, initialFilters);

  // Dropdown choices come from the API so they cover every supervisor, not only
  // the ones on the current page.
  const [filterOptions, setFilterOptions] = useState<{
    enterprises: string[];
    countries: string[];
    languages: string[];
  }>({ enterprises: [], countries: [], languages: [] });

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const response = await api.get("/api/supervisors/filter-options");
        setFilterOptions(response.data);
      } catch (error) {
        console.error("Error loading supervisor filter options:", error);
      }
    };
    loadFilterOptions();
  }, []);

  // Debounced copy of the search box: it is sent to the API, so it must not
  // fire a request on every keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearch(filters.search.trim()),
      300,
    );
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Memoised so the effect below re-runs on a real change, not on every render.
  const activeQuery = useMemo<SupervisorQuery>(
    () => ({
      search: debouncedSearch,
      enterprise: filters.enterprise,
      country: filters.country,
      language: filters.language,
    }),
    [debouncedSearch, filters.enterprise, filters.country, filters.language],
  );

  // Only the very first load shows the page-level spinner. Later fetches must
  // reload the rows in place: the spinner replaces the whole page, which would
  // unmount the search box and take the focus away mid-typing.
  const hasLoadedOnce = useRef(false);

  useEffect(() => {
    fetchSupervisors(currentPage, pageSize, activeQuery, hasLoadedOnce.current);
    hasLoadedOnce.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, activeQuery]);

  /**
   * `silent` reloads the rows in place: no page-level spinner, and a failure is
   * reported with a toast instead of replacing the page with the error view.
   * Used after a sync, where the page is already on screen.
   */
  const fetchSupervisors = async (
    page: number,
    limit: number,
    query: SupervisorQuery,
    silent = false,
  ) => {
    try {
      if (!silent) setLoading(true);
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (query.search) params.append("search", query.search);
      appendEnterpriseFilter(params, query.enterprise);
      if (query.country) params.append("country", query.country);
      if (query.language) params.append("language", query.language);

      const response = await api.get(`/api/supervisors?${params.toString()}`);
      const { data, pagination } = response.data;

      setSupervisors(data);
      setTotalPages(pagination.totalPages);
      setTotalItems(pagination.total);
      setCurrentPage(pagination.page);
    } catch (error) {
      if (silent) {
        toast.error("Erreur lors du rechargement des superviseurs");
      } else {
        setError("Erreur lors du chargement des superviseurs");
      }
      console.error("Error fetching supervisors:", error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const syncSupervisors = async () => {
    try {
      setSyncing(true);
      const data = await syncEntity("supervisors");

      if (data.success) {
        // Refresh the rows in place, keeping the active filters
        await fetchSupervisors(currentPage, pageSize, activeQuery, true);
        toast.success(syncSummary(data));
      } else {
        toast.error("Échec de la synchronisation : " + (data.message ?? ""));
      }
    } catch (error) {
      toast.error("Échec de la synchronisation : " + errorMessage(error));
      console.error("Sync error:", error);
    } finally {
      setSyncing(false);
    }
  };

  const handleEditSupervisor = (supervisor: Supervisor) => {
    setSelectedSupervisor(supervisor);
    setEditModalOpen(true);
  };

  const handleSaveSupervisor = (updatedSupervisor: Supervisor) => {
    setSupervisors((prev) =>
      prev.map((supervisor) =>
        supervisor.id === updatedSupervisor.id ? updatedSupervisor : supervisor,
      ),
    );
    setEditModalOpen(false);
    setSelectedSupervisor(null);
  };

  // Any filter change re-queries the whole table, so the current page number is
  // meaningless against the new result set.
  const handleFilterChange = (key: keyof SupervisorFilters, value: string) => {
    updateFilter(key, value);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    resetFilters();
    setCurrentPage(1);
  };

  // Pagination handlers
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="spinner"></div>
        <p>Chargement des superviseurs...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-error">
        <div className="alert alert-error">{error}</div>
        <button
          className="btn-primary"
          onClick={() => fetchSupervisors(currentPage, pageSize, activeQuery)}
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="supervisors-page">
      <div className="container">
        <div className="page-header">
          <div className="header-content">
            <div>
              <h1 className="page-title">Superviseurs</h1>
            </div>
            <div className="header-actions">
              <button
                className={`sync-button ${syncing ? "syncing" : ""}`}
                onClick={syncSupervisors}
                disabled={syncing}
              >
                {syncing ? "Synchronisation..." : "Synchroniser Superviseurs"}
              </button>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="filters-section">
          <div className="filters-row">
            <div className="filter-group">
              <label>Recherche</label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                placeholder="Nom ou email..."
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label>Entreprise</label>
              <Select
                value={
                  filters.enterprise
                    ? {
                        value: filters.enterprise,
                        label: enterpriseFilterLabel(filters.enterprise),
                      }
                    : null
                }
                onChange={(option) =>
                  handleFilterChange("enterprise", option ? option.value : "")
                }
                // "Sans entreprise" sits alongside the names so the state a dashboard
                // link arrives in is one the dropdown can also express and clear.
                options={enterpriseFilterOptions(
                  filterOptions.enterprises,
                  "Toutes les entreprises",
                )}
                styles={darkSelectStyles}
                placeholder="Toutes les entreprises"
                isClearable
                isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div className="filter-group">
              <label>Pays</label>
              <Select
                value={
                  filters.country
                    ? { value: filters.country, label: filters.country }
                    : null
                }
                onChange={(option) =>
                  handleFilterChange("country", option ? option.value : "")
                }
                options={[
                  { value: "", label: "Tous les pays" },
                  ...filterOptions.countries.map((country) => ({
                    value: country,
                    label: country,
                  })),
                ]}
                styles={darkSelectStyles}
                placeholder="Tous les pays"
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div className="filter-group">
              <label>Langue</label>
              <Select
                value={
                  filters.language
                    ? {
                        value: filters.language,
                        label: formatLanguage(filters.language),
                      }
                    : null
                }
                onChange={(option) =>
                  handleFilterChange("language", option ? option.value : "")
                }
                options={[
                  { value: "", label: "Toutes les langues" },
                  ...filterOptions.languages.map((language) => ({
                    value: language,
                    label: formatLanguage(language),
                  })),
                ]}
                styles={darkSelectStyles}
                placeholder="Toutes les langues"
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div className="filter-group">
              <button
                onClick={handleResetFilters}
                className="btn-secondary reset-filters"
                disabled={!hasActiveFilters}
              >
                Réinitialiser
              </button>
            </div>
          </div>
        </div>

        <div className="page-content">
          <div className="supervisors-table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nom</th>
                  <th>Email</th>
                  <th>Entreprise</th>
                  <th>Pays</th>
                  <th>Langue</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSupervisors.map((supervisor) => (
                  <tr key={supervisor.id}>
                    <td>{supervisor.aia_supervisor_id}</td>
                    <td>
                      {supervisor.first_name} {supervisor.last_name}
                    </td>
                    <td>{supervisor.email}</td>
                    <td>{supervisor.enterprise?.nom || EMPTY_VALUE}</td>
                    <td>{supervisor.enterprise?.country || EMPTY_VALUE}</td>
                    <td>{formatLanguage(supervisor.enterprise?.language)}</td>
                    <td>
                      <button
                        className="btn-edit"
                        onClick={() => handleEditSupervisor(supervisor)}
                        title="Modifier le superviseur"
                      >
                        <HiPencilSquare />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          <div className="pagination-container">
            <div className="pagination-info">
              <span>
                Page {currentPage} sur {totalPages} ({totalItems} superviseurs
                au total)
              </span>
              <div className="page-size-selector">
                <label>Éléments par page</label>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="page-size-select"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              >
                ««
              </button>
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                ‹
              </button>
              <span className="pagination-pages">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      className={`pagination-btn ${
                        currentPage === pageNum ? "active" : ""
                      }`}
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </span>
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                ›
              </button>
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              >
                »»
              </button>
            </div>
          </div>
        </div>
      </div>

      {editModalOpen && selectedSupervisor && (
        <EditModal
          isOpen={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedSupervisor(null);
          }}
          onSave={handleSaveSupervisor}
          type="supervisor"
          data={selectedSupervisor}
        />
      )}
    </div>
  );
};

export default Supervisors;
